import { TextFieldTheme, TextField as TPATextField } from '@wix/wix-ui-tpa/cssVars';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef } from 'react';
import { useConfig } from '../../contexts/config-context';
import { useFocusFieldEvent } from '../../contexts/events-context';
import { ForcedState, useForcedState } from '../../contexts/forced-state-context';
import { useTranslation } from '../../contexts/translation-context';
import { useFocus } from '../../hooks/use-focus';
import { classes } from '../../ui/number-input-field/number-input-field-wut.st.css';
import { getNumberOfDecimals, isNumberInputPossible, isNumberInputValid, toLocaleDecimalSeparator, toCanonicalDecimalSeparator } from '../../ui/shared/numeric-input-utils';
import { useInvokeCallback } from '../../ui/shared/use-invoke-callback';
export const NumberInputWUT = ({ value, validation, required, label, disabled, readOnly, placeholder, onChange, onBlur, onFocus, hasError, inputId, ariaDescribedBy, fieldId })=>{
    const { t } = useTranslation();
    const { regionalFormat } = useConfig();
    const multipleOf = validation?.number?.multipleOf || 1;
    const maximum = validation?.number?.maximum;
    const minimum = validation?.number?.minimum;
    const forcedHover = useForcedState().includes(ForcedState.INPUT_FIELDS_HOVER);
    const _value = toLocaleDecimalSeparator(String(value ?? ''), regionalFormat);
    const [inputValue, setInputValue] = React.useState(_value);
    const invokeBlur = useInvokeCallback(onBlur);
    const inputRef = useRef(null);
    useFocusFieldEvent(()=>{
        inputRef.current?.focus({
            preventScroll: true
        });
    }, fieldId);
    const numberOnChange = useCallback((event)=>{
        const userInput = event.target.value;
        const normalizedValue = toCanonicalDecimalSeparator(userInput);
        const isValid = isNumberInputPossible(normalizedValue, multipleOf);
        if (isValid) {
            setInputValue(userInput);
        }
    }, [
        multipleOf
    ]);
    const handleBlur = useCallback(()=>{
        const normalizedValue = toCanonicalDecimalSeparator(inputValue);
        const newValue = Number(normalizedValue);
        if (inputValue === '' || Number.isNaN(newValue)) {
            onChange('');
            invokeBlur();
            return;
        }
        const numberOfDecimals = getNumberOfDecimals(multipleOf);
        const validValue = isNumberInputValid(newValue, multipleOf) ? newValue : newValue.toFixed(numberOfDecimals);
        onChange(Number(validValue));
        setInputValue(toLocaleDecimalSeparator(String(validValue), regionalFormat));
        invokeBlur();
    }, [
        inputValue,
        multipleOf,
        onChange,
        invokeBlur,
        regionalFormat
    ]);
    useEffect(()=>{
        setInputValue(_value);
    }, [
        _value
    ]);
    const { handleFocus, containerRef } = useFocus({
        onFocus
    });
    return /*#__PURE__*/ React.createElement("div", {
        ref: containerRef,
        onFocus: handleFocus,
        onBlur: handleBlur
    }, /*#__PURE__*/ React.createElement(TPATextField, {
        id: inputId,
        type: "text",
        inputMode: multipleOf ? 'decimal' : 'numeric',
        error: hasError,
        newErrorMessage: true,
        value: inputValue,
        className: classNames(classes.textField, classes.theme, {
            [classes.error]: hasError,
            [classes.hover]: forcedHover
        }),
        onChange: numberOnChange,
        disabled: disabled,
        readOnly: readOnly,
        placeholder: placeholder,
        min: minimum ?? undefined,
        max: maximum ?? undefined,
        "aria-label": label,
        "aria-roledescription": t('field.number.aria-role-description'),
        theme: TextFieldTheme.Box,
        "aria-describedby": ariaDescribedBy,
        "aria-invalid": hasError,
        required: required,
        ref: inputRef
    }));
};

//# sourceMappingURL=number-input-wut.js.map